import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { CheckMini, TrianglesMini } from '@medusajs/icons';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/utils';

/**
 * Alegere dintr-o **listă închisă**, cu căutare la tastare.
 *
 * ## Ce e de la cine
 *
 * **Mecanica e a lui `@base-ui/react`** — `Combobox`-ul lui ţine popup-ul, poziţionarea,
 * navigarea cu tastatura, ARIA şi anunţurile pentru cititoarele de ecran. E aceeaşi primitivă
 * pe care o foloseşte şi shadcn: Radix n-are combobox, iar
 * [pagina lor](https://ui.shadcn.com/docs/components/radix/combobox) importă `@base-ui/react`
 * inclusiv sub tab-ul „Radix UI".
 *
 * **Înfăţişarea e Medusa** — clasele de aici sunt chiar cele ale lui `Select` din
 * `@medusajs/ui`. Nu e preferinţă: controlul stă în **conţinutul unui ecran**, imediat sub un
 * `Select` şi deasupra unui `Input`, iar regula proiectului e „shell-ul e shadcn, conţinutul
 * ecranelor e Medusa" (decisions/index.md#2026-08-13). Prima încercare a fost componenta
 * shadcn copiată întreagă, cu stratul ei de teme `cn-*`: mergea, dar în formular ieşea cu
 * două chenare, alt font şi altă înălţime decât câmpul de deasupra ei — două sisteme de stil
 * lipite, exact ce evită regula.
 *
 * ## De ce nu e un `Select`
 *
 * `Select` nu caută: la douăzeci de opţiuni e o listă de derulat, iar căutarea lui nativă sare
 * la prima literă şi atât. Când catalogul creşte, operatorul nu mai găseşte pachetul — îl
 * caută cu ochii. Aici se scrie **direct în câmp**, iar lista se strânge.
 *
 * ## Lista rămâne închisă
 *
 * `onChange` primeşte doar un `value` din `options`, niciodată textul tastat. Primul
 * consumator e alegerea pachetului pe abonament, iar un pachet inventat în formular ar fi un
 * abonament fără corespondent în catalog.
 *
 * ## Lista poate veni şi de pe server
 *
 * Cu `onSearchChange` + `serverFiltered`, căutarea nu mai e peste `options`, ci peste ce
 * răspunde serverul: consumatorul ascultă tastele, cere lista, şi o dă înapoi ca `options`.
 * Aşa se caută printre lucruri care nu încap în ecran — clienţii, de pildă
 * ([#149](https://github.com/bplugaru/efx-pcrm/issues/149)). Catalogul de pachete rămâne pe
 * filtrarea locală: e mic şi e deja încărcat.
 */

export interface ComboboxOption {
  /** Ce se întoarce prin `onChange`. Nu se caută după el — vezi `matches`. */
  readonly value: string;
  readonly label: string;
  /** Al doilea rând, sub etichetă. Intră şi în căutare (ex. tipul pachetului). */
  readonly description?: string;
  /**
   * Un simbol în faţa etichetei, în listă. **Nu** intră în căutare şi nu apare în câmp după
   * alegere: câmpul e un `<input>` cu text, nu un rând randat.
   *
   * `ReactNode`, nu un nume de icon: `ui-kit` n-are de unde şti ce seturi de iconuri
   * folosesc ecranele, iar o hartă aici ar trebui ţinută la zi cu fiecare.
   */
  readonly icon?: ReactNode;
}

export interface ComboboxProps {
  /** Al câmpului, ca formularul din jur să-şi poată lega eticheta cu `htmlFor`. */
  readonly id?: string;
  /** `''` = nimic ales. */
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly ComboboxOption[];
  readonly placeholder?: string;
  /** Ce se arată când nu mai rămâne nimic după filtrare. */
  readonly emptyText?: string;
  readonly disabled?: boolean;
  /** Câmpul a fost respins de formular. */
  readonly invalid?: boolean;
  readonly required?: boolean;
  readonly className?: string;
  /**
   * Ce s-a tastat în câmp, la fiecare tastă.
   *
   * Se dă când lista **vine de pe server** filtrată: consumatorul ţine textul, îl trimite mai
   * departe cu un răgaz, şi reîmprospătează `options`.
   */
  readonly onSearchChange?: (search: string) => void;
  /**
   * Lista primită e **deja filtrată**, deci filtrarea locală se opreşte.
   *
   * ⚠️ **Fără asta, o căutare pe server minte.** `options` poartă atunci doar rândurile
   * potrivite cu ultimul termen trimis; filtrate încă o dată local, cu textul de acum, ele
   * dispar între două tastări — panoul scrie „Nimic găsit" pentru un rând care chiar există,
   * iar cine se uită dă vina pe server.
   */
  readonly serverFiltered?: boolean;
}

/**
 * Textul, pregătit de comparat: fără diacritice, litere mici.
 *
 * „Telefonie fixă" trebuie găsit scriind „fixa" — operatorul tastează repede şi rar cu
 * diacritice. NFD desface `ă` în `a` + semn, iar semnul cade la filtrare.
 */
function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Toate cuvintele tastate, oriunde în text — nu prefixul întreg.
 *
 * „int 300" găseşte „Internet 300", care e chiar felul în care se scrie când ţinta e ştiută pe
 * jumătate. Potrivire pe subşir, nu fuzzy: fuzzy-ul ar accepta şi denumiri fără nicio
 * legătură, iar aici lista trebuie să inspire încredere.
 */
export function matches(haystack: string, search: string): boolean {
  const target = fold(haystack);
  return fold(search)
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .every((word) => target.includes(word));
}

export function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = 'Alege',
  emptyText = 'Nimic găsit.',
  disabled = false,
  invalid = false,
  required = false,
  className,
  onSearchChange,
  serverFiltered = false,
}: ComboboxProps) {
  // `null`, nu `''`: pentru Base UI „nimic ales" e absenţa valorii, iar şirul gol ar fi o
  // opţiune cu eticheta goală — câmpul ar arăta gol şi ar părea totuşi completat.
  const selected = useMemo(
    () => options.find((option) => option.value === value) ?? null,
    [options, value],
  );

  const field = useRef<HTMLDivElement>(null);
  /**
   * Unde se deschide panoul: **în dialogul care conţine controlul**, dacă există unul.
   *
   * Formularele stau în `Drawer` — un dialog modal Radix, care pune `pointer-events: none` pe
   * `body` cât e deschis. Panoul, portat direct în `body`, se vede dar e **mort**: lista se
   * filtrează, săgeţile mută selecţia, iar clicul pe un rând nu face nimic. Înăuntrul
   * dialogului îşi primeşte `pointer-events` înapoi.
   *
   * Se citeşte la deschidere, nu la montare: `field` are element abia după prima randare.
   */
  const [container, setContainer] = useState<HTMLElement | undefined>(undefined);

  return (
    <ComboboxPrimitive.Root
      onOpenChange={(open: boolean) => {
        if (open) {
          // `undefined`, nu `null`, când nu există dialog: pentru Base UI `null` înseamnă
          // „niciun container", iar panoul n-ar mai fi randat deloc.
          setContainer(field.current?.closest<HTMLElement>('[role="dialog"]') ?? undefined);
        }
      }}
      items={options as ComboboxOption[]}
      value={selected}
      onValueChange={(next: ComboboxOption | null) => onChange(next?.value ?? '')}
      // Ce se scrie în câmp pentru opţiunea aleasă. Fără asta, câmpul ar arăta obiectul.
      itemToStringLabel={(option: ComboboxOption) => option.label}
      // Filtrarea e a noastră: cea implicită caută doar în eticheta din câmp, iar aici trebuie
      // să prindă şi rândul secundar (tipul pachetului).
      //
      // `null` o opreşte de tot, când lista vine filtrată de pe server — vezi `serverFiltered`.
      filter={
        serverFiltered
          ? null
          : (option: ComboboxOption, query: string) =>
              matches(`${option.label} ${option.description ?? ''}`, query)
      }
      onInputValueChange={(search: string) => onSearchChange?.(search)}
      disabled={disabled}
    >
      <div ref={field} className="relative w-full">
        {/*
          Clasele lui `Select.Trigger` din Medusa, ca să fie acelaşi câmp cu cel de deasupra:
          aceeaşi înălţime, acelaşi fundal, aceeaşi umbră de focus. `pe-8` lasă loc săgeţii.
        */}
        <ComboboxPrimitive.Input
          id={id}
          placeholder={placeholder}
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          className={cn(
            'transition-fg txt-compact-small flex h-8 w-full select-none items-center rounded-md bg-ui-bg-field px-2 py-1.5 pe-8 text-ui-fg-base shadow-buttons-neutral outline-none placeholder:text-ui-fg-muted',
            'focus:shadow-borders-interactive-with-active',
            'aria-[invalid=true]:shadow-borders-error',
            'disabled:!bg-ui-bg-disabled disabled:!text-ui-fg-disabled',
            className,
          )}
        />
        {/* Aceeaşi săgeată ca la `Select`, în interiorul câmpului — nu într-un buton lipit. */}
        <ComboboxPrimitive.Trigger
          aria-label="Arată pachetele"
          disabled={disabled}
          className="absolute inset-y-0 end-0 flex items-center px-2 text-ui-fg-muted outline-none disabled:text-ui-fg-disabled"
        >
          <TrianglesMini />
        </ComboboxPrimitive.Trigger>
      </div>

      <ComboboxPrimitive.Portal container={container}>
        {/* `z-50`: panoul se deschide din interiorul unui `Drawer`, care stă deja peste ecran. */}
        <ComboboxPrimitive.Positioner sideOffset={8} className="isolate z-50">
          {/*
            Panoul e pe `bg-ui-bg-base` (alb), nu pe `bg-ui-bg-component` ca `Select`.
            În tema de aici, `--bg-component` şi `--bg-*-hover` sunt remapate pe **aceeaşi**
            valoare (puntea Medusa aliniază neutrele la tokenii shell-ului), deci un rând
            evidenţiat peste un panou `component` e invizibil. Alb + `base-hover` e singura
            pereche cu contrast real. Vezi styles/medusa-bridge.css.
          */}
          <ComboboxPrimitive.Popup className="w-(--anchor-width) max-h-(--available-height) overflow-hidden rounded-lg border border-ui-border-base bg-ui-bg-base p-1 text-ui-fg-base shadow-elevation-flyout">
            {/*
              Spaţierea stă pe **copil**, nu pe rădăcină. Base UI cere ca elementul să rămână
              montat (aşa anunţă schimbările la cititoarele de ecran) şi randează doar
              copilul, condiţionat — cu `py` pe rădăcină, ţinea o bandă goală în capul listei
              tot timpul.
            */}
            <ComboboxPrimitive.Empty>
              <span className="txt-compact-small block px-2 py-3 text-center text-ui-fg-subtle">
                {emptyText}
              </span>
            </ComboboxPrimitive.Empty>
            <ComboboxPrimitive.List className="max-h-50 overflow-y-auto overscroll-contain">
              {(option: ComboboxOption) => (
                <ComboboxPrimitive.Item
                  key={option.value}
                  value={option}
                  // Clasele lui `Select.Item`: coloana de 15px e locul bifei, ca rândurile să
                  // se alinieze şi când niciunul nu e ales.
                  //
                  // `data-highlighted` acoperă şi săgeţile, şi mouse-ul — aici se navighează
                  // mai ales de la tastatură, deci rândul curent trebuie să se vadă fără
                  // cursor pe el.
                  className="txt-compact-small grid cursor-pointer grid-cols-[15px_1fr] items-center gap-x-2 rounded-[4px] px-2 py-1 text-ui-fg-base outline-none transition-colors data-highlighted:bg-ui-bg-base-hover data-selected:txt-compact-small-plus"
                >
                  {/*
                    Bifa stă într-un `span` **care există mereu**, ca la `Select` din Medusa.
                    `ItemIndicator` nu randează nimic cât rândul nu e ales; pus direct în
                    grilă, rândul rămânea cu un singur copil, iar denumirea cădea în coloana
                    de 15px a bifei şi se tăia la o literă.
                  */}
                  <span className="flex h-[15px] w-[15px] items-center justify-center">
                    <ComboboxPrimitive.ItemIndicator>
                      <CheckMini />
                    </ComboboxPrimitive.ItemIndicator>
                  </span>
                  {/*
                    Iconul stă **în aceeaşi coloană** cu textul, nu într-una a lui: altfel
                    rândurile fără icon ar începe decalat faţă de cele cu, iar lista s-ar citi
                    ca două liste lipite.
                  */}
                  <span className="flex min-w-0 items-center gap-x-2">
                    {option.icon ? (
                      <span className="flex shrink-0 items-center text-ui-fg-subtle">
                        {option.icon}
                      </span>
                    ) : null}
                    <span className="flex min-w-0 flex-col leading-tight">
                      <span className="truncate">{option.label}</span>
                      {option.description ? (
                        <span className="txt-compact-xsmall truncate leading-tight text-ui-fg-muted">
                          {option.description}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </ComboboxPrimitive.Item>
              )}
            </ComboboxPrimitive.List>
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </ComboboxPrimitive.Root>
  );
}
